import { Button, DatePicker, Descriptions, Divider, Flex, Input, List, Select, Spin, Switch } from "antd"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import isEqual from 'lodash/isEqual'
import { DepartmentContext, UpdateDepartmentContext } from "../../../../../context/departmentContext"
import { UpdateUsersContext, UserRequestType, UsersContext } from "../../../../../context/userContext"
import dayjs from "dayjs"
import { CompanyContext, UpdateCompanyContext } from "../../../../../context/companyContext"
import { App } from 'antd'
import { isValidEmail } from "../../../../../utils/validation"
import { buildAdminUserNavigationState, buildDepartmentUserDetailPath, PATH_INFO, resolveAdminUserBackToPath, resolveAdminUserReturnToPath, shouldUseAdminUserHistoryBack } from "../../../../util/helper"

const defaultUser: UserRequestType = {
	departmentId: 0,
	position: '',
	name: '',
	email: '',
	start_date: '',
	is_admin: false,
}

export function DepartmentUserManage() {
	const { refreshDepartmentList } = useContext(UpdateDepartmentContext)
	const { departmentList } = useContext(DepartmentContext)
	const { registerUser, refreshUser, updateUser, reissueUserPinCode, reissueUserPassword } = useContext(UpdateUsersContext)
	const { refreshCompany } = useContext(UpdateCompanyContext)
	const { company } = useContext(CompanyContext)
	const { refreshDepartmentDetail } = useContext(UpdateDepartmentContext)
	const { departmentDetail } = useContext(DepartmentContext)
	const { user } = useContext(UsersContext)
	const { companyId, departmentId, userId } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const backTo = resolveAdminUserBackToPath(location.state, companyId, departmentId)
	const returnTo = resolveAdminUserReturnToPath(location.state, companyId, departmentId, userId)
	const useHistoryBack = shouldUseAdminUserHistoryBack(location.state)
	const shouldUseHistoryBackOnManage = useMemo(
		() => useHistoryBack && returnTo === backTo,
		[backTo, returnTo, useHistoryBack]
	)

	const [userInfo, setUserInfo] = useState<UserRequestType>(defaultUser)
	const [errors, setErrors] = useState<Record<string, boolean>>({})
	const [isLoading, setIsLoading] = useState(false)
	const [isUnchanged, setIsUnchanged] = useState(true)

	const { modal } = App.useApp()

	useEffect(() => {
		if (!companyId || !userId) return
		refreshDepartmentList(companyId)
		refreshCompany(companyId)
		if (!departmentId || departmentId === 'undefined') return
		refreshDepartmentDetail(departmentId)
		if (userId === 'register') return
		refreshUser(userId)
	}, [companyId, userId, departmentId, refreshDepartmentList, refreshCompany, refreshUser, refreshDepartmentDetail])

	useEffect(() => {
		if (userId === 'register') {
			if (departmentId) setUserInfo((prev) => ({ ...prev, departmentId: parseInt(departmentId) }))
			setUserInfo((prev) => ({ ...prev, start_date: dayjs().toString() }))
			return
		} else {
			setUserInfo({
				departmentId: user.department.id || 0,
				position: user.position || '',
				name: user.name,
				email: user.email,
				start_date: user.start_date,
				is_admin: user.is_admin ?? false
			})
		}
	}, [user, userId, departmentId])

	useEffect(() => {
		if (userId === 'register') {
			setIsUnchanged(false)
			return
		}
		if (!user?.department) {
			setIsUnchanged(true)
			return
		}
		const originalUserInfo = {
			departmentId: user.department.id || 0,
			position: user.position || '',
			name: user.name,
			email: user.email,
			start_date: user.start_date,
			is_admin: user.is_admin ?? false
		}
		setIsUnchanged(isEqual(userInfo, originalUserInfo))
	}, [userInfo, user, userId])

	const inputPlaceHolder = { title: `${company?.company_name}${(departmentId !== 'undefined') ? ` / ${departmentDetail.name}` : ''}`, column: ['部署名', '氏名', '役職名', 'メールアドレス', 'PIN', '利用開始日'] }

	const onClickBack = useCallback(() => {
		if (shouldUseHistoryBackOnManage) {
			navigate(-1)
			return
		}
		const destination = returnTo ?? backTo
		if (destination) {
			navigate(destination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
			return
		}
		navigate(-1)
	}, [backTo, navigate, returnTo, shouldUseHistoryBackOnManage, useHistoryBack])

	const navigateAfterSubmit = useCallback((nextDepartmentId: number) => {
		const targetUserId = userId?.trim()
		const currentUserDetailPath = buildDepartmentUserDetailPath(companyId, departmentId, targetUserId)
		const shouldReturnToUserDetail = Boolean(
			targetUserId
			&& returnTo
			&& currentUserDetailPath
			&& returnTo === currentUserDetailPath
		)

		if (
			targetUserId !== 'register'
			&& shouldReturnToUserDetail
			&& companyId
			&& targetUserId
			&& Number(departmentId) !== nextDepartmentId
		) {
			const destination = buildDepartmentUserDetailPath(companyId, `${nextDepartmentId}`, targetUserId)
			if (!destination) {
				const fallbackDestination = returnTo ?? backTo
				if (fallbackDestination) {
					navigate(fallbackDestination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
					return
				}
				navigate(PATH_INFO.ERROR.PATH, { replace: true })
				return
			}
			navigate(destination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
			return
		}

		if (shouldUseHistoryBackOnManage) {
			navigate(-1)
			return
		}

		const destination = returnTo ?? backTo
		if (destination) {
			navigate(destination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
			return
		}

		navigate(-1)
	}, [backTo, companyId, departmentId, navigate, returnTo, shouldUseHistoryBackOnManage, useHistoryBack, userId])

	const onClickSubmit = useCallback(async () => {
		if (!userId) return

		const newErrors: Record<string, boolean> = {};

		// 必須チェック
		['departmentId', 'name', 'email', 'start_date'].forEach((key) => {
			if (!userInfo[key as keyof UserRequestType]) {
				newErrors[key] = true
			}
		})
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			modal.error({
				content: '入力内容を確認してください。',
				centered: true,
			})
			return
		}

		// メールアドレスの形式チェック
		if (!isValidEmail(userInfo.email)) {
			newErrors.email = true
			setErrors(newErrors)
			modal.error({
				content: 'メールアドレスの形式が正しくありません。',
				centered: true,
			})
			return
		}

		setErrors({})
		setIsLoading(true)

		try {
			if (userId === 'register') {
				const data: UserRequestType = {
					...userInfo,
					position: userInfo.position || null
				}
				const res = await registerUser(data)
				if (res[1]) {
					modal.success({ content: res[0], centered: true, onOk: () => navigateAfterSubmit(userInfo.departmentId) })
				} else {
					modal.error({ content: res[0], centered: true })
				}
			} else {
				// 編集時の更新チェック
				if (isUnchanged) return // isUnchangedがtrueの場合は何もしない

				const data: UserRequestType = {
					...userInfo,
					position: userInfo.position || null
				}
				const res = await updateUser(data, userId)
				if (res[1]) {
					modal.success({ content: res[0], centered: true, onOk: () => navigateAfterSubmit(userInfo.departmentId) })
				} else {
					modal.error({ content: res[0], centered: true })
				}
			}
		} catch (error) {
			modal.error({ content: '処理中にエラーが発生しました。', centered: true })
		} finally {
			setIsLoading(false)
		}
	}, [registerUser, updateUser, userInfo, userId, modal, navigateAfterSubmit, isUnchanged])

	const onClickReissue = useCallback(async (type: string) => {
		if (!userId) return
		if (userId !== 'register') {
			setIsLoading(true)
			if (type === 'pin') {
				try {
					await reissueUserPinCode(userId)
					modal.success({
						title: 'PINの再発行が完了しました。',
						content: '新しいPINコードが保存されました。',
						centered: true,
					})
				} catch (error) {
					modal.error({
						title: 'PINの再発行に失敗しました。',
						content: '再度お試しください。',
						centered: true,
					})
				} finally {
					setIsLoading(false)
				}
			} else if (type === 'password') {
				try {
					await reissueUserPassword(userId)
					modal.success({
						title: 'パスワードの再発行が完了しました。',
						content: '新しいパスワードが保存されました。',
						centered: true,
					})
				} catch (error) {
					modal.error({
						title: 'パスワードの再発行に失敗しました。',
						content: '再度お試しください。',
						centered: true,
					})
				} finally {
					setIsLoading(false)
				}
			}
		}
	}, [userId, reissueUserPinCode, reissueUserPassword, modal])

	const dataSource = useMemo(() => {
		const data: {
			key: string;
			label: string;
			value?: string | number | boolean;
			type: string;
			disabled?: boolean;
			onchange?: (value: number | string | boolean | React.ChangeEvent<HTMLInputElement>) => void
		}[] = [
				{
					key: 'departmentName',
					label: '部署名',
					value: userInfo.departmentId,
					type: 'select',
					onchange: (value) => {
						if (typeof value === 'string' || typeof value === 'number') {
							setUserInfo((prev) => ({
								...prev,
								departmentId: typeof value === 'string' ? Number(value) : value
							}))
						}
					}
				},
				{
					key: 'personInCharge',
					label: '役職',
					value: userInfo.position || '',
					type: 'text',
					onchange: (e) => { setUserInfo((prev) => ({ ...prev, position: (e as React.ChangeEvent<HTMLInputElement>).target.value })) }
				},
				{
					key: 'name',
					label: '氏名',
					value: userInfo.name,
					type: 'text',
					onchange: (e) => { setUserInfo((prev) => ({ ...prev, name: (e as React.ChangeEvent<HTMLInputElement>).target.value })) }
				},
				{
					key: 'email',
					label: 'メールアドレス',
					value: userInfo.email,
					type: 'email',
					onchange: (e) => { setUserInfo((prev) => ({ ...prev, email: (e as React.ChangeEvent<HTMLInputElement>).target.value })) }
				},
				{
					key: 'is_admin',
					label: '管理者権限',
					value: userInfo.is_admin ?? false,
					type: 'switch',
					onchange: (value) => {
						if (typeof value === 'boolean') {
							setUserInfo((prev) => ({ ...prev, is_admin: value }))
						}
					}
				},
				{
					key: 'start_at',
					label: '利用開始日',
					value: userInfo.start_date,
					type: 'date',
					onchange: (value) => { setUserInfo((prev) => ({ ...prev, start_date: value as string })) }
				}
			]

		if (userId !== 'register') {
			const pinObj = {
				key: 'pin_code',
				label: 'PIN',
				value: user.pin_code,
				type: 'pin',
				disabled: true
			}
			const passwordObj = {
				key: 'password',
				label: 'パスワード',
				value: '●●●●●●●',
				type: 'password',
				disabled: true,
			}
			data.splice(3, 0, pinObj)
			data.splice(4, 0, passwordObj)
		}

		return data
	}, [userInfo, userId, user])

	return (
		<div className="relative">
			<Spin spinning={isLoading} tip="処理中..." size="large" fullscreen />
			<div>
				<div className="flex w-full space-x-5 items-center">
					<p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">ユーザ{userId === 'register' ? '登録' : '編集'}画面</p>
				</div>
				<Divider />
				<Flex vertical align="center">
					<List
						header={<div className="font-bold break-all">{inputPlaceHolder.title}</div>}
						bordered
						dataSource={dataSource}
						renderItem={(department) => (
							<List.Item style={{ background: '#FFFFFF', justifyContent: 'end' }} >
								<Descriptions column={1} size="small" bordered>
									<Descriptions.Item label={department.label} key={department.key} >
										{department.type === 'select' && (
											<Select
												className="w-[320px] text-start"
												options={departmentList.map((el) => {
													return { value: el.id, label: <span>{el.name}</span> }
												})}
												onChange={(value) => department.onchange?.(value)}
												value={userInfo.departmentId || ''}
												status={errors.departmentId ? 'error' : ''}
											/>
										)}
										{(department.type === 'text' || department.type === 'email') && (
											<Input
												type={department.type}
												value={(typeof department.value === 'string' || typeof department.value === 'number') ? department.value : ''}
												disabled={department.disabled}
												onChange={(e) => department.onchange?.(e)}
												className="w-[320px]"
												status={errors[department.key] ? 'error' : ''}
											/>
										)}
										{(department.type === 'pin' || department.type === 'password') && (
											<div className="flex w-[320px]">
												<Input
													type="text"
													value={(typeof department.value === 'string' || typeof department.value === 'number') ? department.value : ''}
													disabled={department.disabled}
													className="mr-2"
												/>
												<Button
													variant="outlined"
													color="primary"
													onClick={() => {
														modal.confirm({
															title: `本当に${department.type === 'pin' ? 'PIN' : 'パスワード'}を再発行しますか？`,
															content:
																<div className="flex flex-col">
																	<span>再発行すると現在の{department.type === 'pin' ? 'PIN' : 'パスワード'}は無効になります。</span>
																</div>,
															onOk: async () => {
																await onClickReissue(department.type)
															},
															centered: true,
														})
													}}
												>
													再発行
												</Button>
											</div>
										)}
										{department.type === 'date' && (
											<DatePicker
												value={(typeof department.value === 'string' || typeof department.value === 'number') ? dayjs(department.value) : undefined}
												onChange={(_, dateString) => {
													if (Array.isArray(dateString)) {
														department.onchange?.(dateString[0])
													} else {
														department.onchange?.(dateString)
													}
												}}
												className="w-[320px]"
												status={errors.start_date ? 'error' : ''}
											/>
										)}
										{department.type === 'switch' && (
											<Flex className="w-[320px]" justify="start">
												<Switch
													checked={Boolean(department.value)}
													onChange={(checked) => department.onchange?.(checked)}
												/>
											</Flex>
										)}
									</Descriptions.Item>
								</Descriptions>
							</List.Item>
						)}
						className="overflow-hidden bg-[#FAFAFA] text-center"
					/>
					<Flex gap="large" className="mt-5">
						<Button variant="outlined" color="primary" size="large" onClick={onClickBack} disabled={isLoading}>戻る</Button>
						<Button type="primary" size="large" onClick={onClickSubmit} loading={isLoading} disabled={isLoading || isUnchanged}>{userId === 'register' ? '登録' : '更新'}</Button>
					</Flex>
				</Flex>
			</div>
		</div>
	)
}
